import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import type { Translations } from './types.js'
import { fr } from './fr.js'
import { apiFetch } from '../hooks/useApiBase.js'

// FR is bundled (default), all others are lazy-loaded
export type Locale = 'fr' | 'nl' | 'de' | 'en' | 'es' | 'it' | 'pt' | 'pl' | 'ro' | 'cs' | 'el' | 'da' | 'sv' | 'fi'

export const SUPPORTED_LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
  { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
]

const LOCALE_CODES = new Set(SUPPORTED_LOCALES.map(l => l.code))

// Lazy loaders — only FR is bundled, others load on demand
const loaders: Record<Locale, () => Promise<Translations>> = {
  fr: async () => fr,
  nl: async () => (await import('./nl.js')).nl,
  de: async () => (await import('./de.js')).de,
  en: async () => (await import('./en.js')).en,
  es: async () => (await import('./es.js')).es,
  it: async () => (await import('./it.js')).it,
  pt: async () => (await import('./pt.js')).pt,
  pl: async () => (await import('./pl.js')).pl,
  ro: async () => (await import('./ro.js')).ro,
  cs: async () => (await import('./cs.js')).cs,
  el: async () => (await import('./el.js')).el,
  da: async () => (await import('./da.js')).da,
  sv: async () => (await import('./sv.js')).sv,
  fi: async () => (await import('./fi.js')).fi,
}

// Cache loaded translations
const cache: Partial<Record<Locale, Translations>> = { fr }

interface I18nContextValue {
  locale: Locale
  setLocale: (l: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'fr',
  setLocale: () => {},
  t: (key) => key,
})

function resolve(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.')
  let cur: unknown = obj
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p]
    } else {
      return path // fallback: return key
    }
  }
  return typeof cur === 'string' ? cur : path
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem('wastesite-locale')
    if (stored && LOCALE_CODES.has(stored as Locale)) return stored as Locale
    // Auto-detect browser locale
    const browserLang = navigator.language.split('-')[0] as Locale
    if (LOCALE_CODES.has(browserLang)) return browserLang
    return 'fr'
  })
  const [, forceUpdate] = useState(0)
  const loadingRef = useRef(false)

  // Load translations for current locale
  useEffect(() => {
    if (cache[locale] || loadingRef.current) return
    loadingRef.current = true
    loaders[locale]().then(translations => {
      cache[locale] = translations
      loadingRef.current = false
      forceUpdate(n => n + 1)
    }).catch(() => {
      loadingRef.current = false
    })
  }, [locale])

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l)
    localStorage.setItem('wastesite-locale', l)
    document.documentElement.lang = l
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const translations = cache[locale] || cache.fr!
    let str = resolve(translations as unknown as Record<string, unknown>, key)
    // Fallback to French if key not found in current locale
    if (str === key && locale !== 'fr') {
      str = resolve(cache.fr as unknown as Record<string, unknown>, key)
    }
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        str = str.replaceAll(`{${k}}`, String(v))
      }
    }
    return str
  }, [locale])

  // Sync with settings API language preference
  useEffect(() => {
    apiFetch<{ data: { language?: string } }>('/api/settings')
      .then(data => {
        const lang = data?.data?.language
        if (lang && LOCALE_CODES.has(lang as Locale) && lang !== locale) {
          setLocale(lang as Locale)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = () => useContext(I18nContext)
export const useT = () => useContext(I18nContext).t
